import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

// Animated number counter that runs when scrolled into view.
export const StatCounter = ({ value, suffix = "", label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);
  const target = parseInt(value, 10) || 0;

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1200;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return () => { start = target; };
  }, [inView, target]);

  return (
    <div ref={ref} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="text-center md:text-left">
      <div className="font-anton text-6xl leading-none text-white md:text-7xl">
        {display}
        <span className="text-kbyellow">{suffix}</span>
      </div>
      <div className="mt-2 font-display text-lg tracking-[0.2em] text-white/50">{label}</div>
    </div>
  );
};

// Interior page hero band with image + skewed accents.
export const PageHero = ({ eyebrow, title, subtitle, image, testid = "page-hero" }) => {
  return (
    <section data-testid={testid} className="relative flex h-[62vh] min-h-[440px] w-full items-end overflow-hidden">
      <motion.img
        src={image}
        alt={title}
        className="absolute inset-0 h-full w-full object-cover"
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-black/30" />
      <div className="grain-overlay" />
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-16 md:px-10 md:pb-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-4 flex items-center gap-3"
        >
          <span className="h-[2px] w-10 bg-kbyellow" />
          <span className="font-display text-base tracking-[0.3em] text-kbcyan">{eyebrow}</span>
        </motion.div>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-6xl leading-[0.85] tracking-tight text-white md:text-8xl"
          >
            {title}
          </motion.h1>
        </div>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </section>
  );
};

export const SectionTitle = ({ eyebrow, title, className = "", align = "left" }) => (
  <div className={`${align === "center" ? "text-center" : "text-left"} ${className}`}>
    {eyebrow && (
      <div className={`mb-3 flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
        <span className="h-[2px] w-10 bg-kbyellow" />
        <span className="font-display text-base tracking-[0.3em] text-kbcyan">{eyebrow}</span>
      </div>
    )}
    <h2 className="font-display text-5xl leading-[0.9] tracking-tight text-white md:text-7xl">{title}</h2>
  </div>
);

export default PageHero;
